'use client';

import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';

import type { HeaderSearchResults, SearchLink } from '../header-search';
import { SearchProductRow } from './search-product-row';
import type { SearchPanelData } from './search-types';
import { columnTracks, OPTION, SearchChips, SearchGroup, SearchLinkRow } from './search-ui';

/** „Produkty ({pocet})“ / „Zobraziť všetky výsledky ({pocet})“ with the core words as fallback. */
export function useResultTexts(texts: SearchPanelData['texts'], total: number) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const pocet = total.toLocaleString(locale);
  return {
    products: fillTemplate(texts.productsTitle || t('text150'), { pocet }),
    all: fillTemplate(texts.allResults || t('text670'), { pocet }),
  };
}

function LinkRows({
  links,
  icon,
  onSelect,
}: {
  links: SearchLink[];
  icon: 'printer' | 'article';
  onSelect: () => void;
}) {
  return (
    <ul role="list" className="flex flex-col gap-2">
      {links.map(link => (
        <li key={link.id}>
          <SearchLinkRow
            href={link.href}
            icon={<Icon name={icon} size={20} className="text-[#127A26]" />}
            title={link.name}
            onClick={onSelect}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * Figma 185:14902 „Body“ for a typed query: „Tlačiarne“, „Kategórie“ (chips) and „Poradňa“ on the
 * left (420 of 1328), „Produkty (n)“ rows and the full-width „Zobraziť všetky výsledky (n)“ on the
 * right; a side without results is left out, the other takes the width. The sheet stacks them.
 */
export function SearchResults({
  data,
  layout,
  results,
  query,
  isPending,
  onSelect,
}: {
  data: SearchPanelData;
  layout: 'columns' | 'stack';
  /** Undefined until the first answer for this panel. */
  results: HeaderSearchResults | undefined;
  /** The query the results belong to (bold in the product names, „všetky výsledky“). */
  query: string;
  isPending: boolean;
  /** A result was picked: remember the query and close. */
  onSelect: () => void;
}) {
  const { t } = useTranslations();
  const labels = useResultTexts(data.texts, results?.total ?? 0);

  const spinner = (
    <div className="flex h-24 items-center justify-center text-[#5D655D]">
      <Spinner size="lg" />
    </div>
  );
  if (!results) return spinner;

  const { printers, categories, articles, products } = results;
  const hasSuggestions = printers.length > 0 || categories.length > 0 || articles.length > 0;

  if (!hasSuggestions && !products.length) {
    return isPending ? (
      spinner
    ) : (
      <p className="nt-body-m px-4 py-6 text-[#5D655D] sm:px-6">{t('text554')}</p>
    );
  }

  const suggestions = hasSuggestions ? (
    <>
      {printers.length ? (
        <SearchGroup title={data.texts.printersTitle}>
          <LinkRows links={printers} icon="printer" onSelect={onSelect} />
        </SearchGroup>
      ) : null}
      {categories.length ? (
        <SearchGroup title={t('text176')}>
          <SearchChips
            chips={categories.map(category => ({
              key: category.id,
              label: category.name,
              href: category.href,
            }))}
            onSelect={onSelect}
          />
        </SearchGroup>
      ) : null}
      {articles.length ? (
        <SearchGroup title={t('text475')}>
          <LinkRows links={articles} icon="article" onSelect={onSelect} />
        </SearchGroup>
      ) : null}
    </>
  ) : null;

  const productList = products.length ? (
    <>
      <SearchGroup title={labels.products}>
        <ul role="list" className="flex flex-col gap-2 @container">
          {products.map(product => (
            <SearchProductRow
              key={product.id}
              product={product}
              query={query}
              onSelect={onSelect}
            />
          ))}
        </ul>
      </SearchGroup>
      <Button asChild size="md" className="w-full">
        <Link {...OPTION} href={ROUTES.SEARCH(query)} onClick={onSelect}>
          {labels.all}
        </Link>
      </Button>
    </>
  ) : null;

  if (layout === 'stack') {
    return (
      <div aria-busy={isPending} className="flex flex-col gap-6 px-4 py-6 sm:px-6">
        {suggestions}
        {productList ? <div className="flex flex-col gap-2">{productList}</div> : null}
      </div>
    );
  }

  return (
    <div
      aria-busy={isPending}
      className="grid divide-x divide-[#E2E5DF]"
      style={{
        gridTemplateColumns: columnTracks(
          [suggestions && 420, productList && 908].filter((width): width is number => !!width),
        ),
      }}
    >
      {suggestions ? <div className="flex flex-col gap-6 p-6">{suggestions}</div> : null}
      {productList ? <div className="flex flex-col gap-2 p-6">{productList}</div> : null}
    </div>
  );
}
